import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { cn } from "../../lib/cn";

/**
 * Toast — notifications éphémères (succès d'action admin, erreurs). Provider + hook.
 */

export type ToastTone = "success" | "error" | "info" | "arcane";

interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  message?: string;
}

interface ToastApi {
  push: (t: Omit<ToastItem, "id">) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_STYLE: Record<ToastTone, string> = {
  success: "border-emerald-400/50 shadow-[0_0_18px_-6px_#34d399]",
  error: "border-blood-400/60 shadow-glow-blood",
  info: "border-arcane-400/50 shadow-glow-arcane",
  arcane: "border-violet-400/50 shadow-glow-violet",
};

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (t: Omit<ToastItem, "id">) => {
      const id = ++counter;
      setItems((prev) => [...prev, { ...t, id }]);
      window.setTimeout(() => remove(id), 5000);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (title, message) => push({ tone: "success", title, ...(message ? { message } : {}) }),
      error: (title, message) => push({ tone: "error", title, ...(message ? { message } : {}) }),
      info: (title, message) => push({ tone: "info", title, ...(message ? { message } : {}) }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cn(
              "pointer-events-auto animate-fade-in rounded border bg-obsidian-200 p-3",
              TONE_STYLE[t.tone],
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-display text-sm font-semibold text-parchment-100">{t.title}</p>
              <button
                onClick={() => remove(t.id)}
                className="text-parchment-200/40 hover:text-parchment-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>
            {t.message && <p className="mt-0.5 text-xs text-parchment-200/65">{t.message}</p>}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast doit être utilisé dans un <ToastProvider>.");
  return ctx;
}
